/**
 * Settings Page
 * 
 * Teams Hub preferences and notification settings.
 */

import { useState } from 'react'
import { 
  Settings, 
  Bell,
  BellOff,
  Moon,
  Sun,
  Globe,
  Volume2,
  VolumeX,
  Eye,
  EyeOff,
  Save,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '../lib/utils'
import { useTeamsStore } from '../store/teamsStore'

export default function SettingsPage() {
  const { mutedChannels, myStatus, setMyStatus } = useTeamsStore()
  const [soundEnabled, setSoundEnabled] = useState(true)
  const [desktopNotifications, setDesktopNotifications] = useState(true)
  const [showTypingIndicator, setShowTypingIndicator] = useState(true)
  const [showOnlineStatus, setShowOnlineStatus] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  
  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Save settings (would call API in real implementation)
      await new Promise(resolve => setTimeout(resolve, 500))
      toast.success('Settings saved!')
    } catch (e) {
      toast.error('Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      <div className="max-w-2xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Settings</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Manage your Teams Hub preferences
          </p>
        </div>
        
        {/* Settings Sections */}
        <div className="space-y-6">
          {/* Status */}
          <SettingsSection title="Status" icon={Globe}>
            <div className="space-y-3">
              <p className="text-sm text-slate-500 mb-3">Set your presence status</p>
              <div className="grid grid-cols-2 gap-2">
                {(['online', 'away', 'busy', 'offline'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setMyStatus(status)}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border-2 transition-all",
                      myStatus === status
                        ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
                        : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                    )}
                  >
                    <div className={cn(
                      "w-3 h-3 rounded-full",
                      status === 'online' && "bg-emerald-500",
                      status === 'away' && "bg-amber-500",
                      status === 'busy' && "bg-red-500",
                      status === 'offline' && "bg-slate-400"
                    )} />
                    <span className="text-sm font-medium capitalize">{status}</span>
                  </button>
                ))}
              </div>
            </div>
          </SettingsSection>
          
          {/* Notifications */}
          <SettingsSection title="Notifications" icon={Bell}>
            <div className="space-y-4">
              <SettingsToggle
                label="Desktop Notifications"
                description="Show notifications on your desktop"
                icon={desktopNotifications ? Bell : BellOff}
                enabled={desktopNotifications}
                onChange={setDesktopNotifications}
              />
              <SettingsToggle
                label="Notification Sounds"
                description="Play sounds for new messages"
                icon={soundEnabled ? Volume2 : VolumeX}
                enabled={soundEnabled}
                onChange={setSoundEnabled}
              />
            </div>
          </SettingsSection>
          
          {/* Privacy */}
          <SettingsSection title="Privacy" icon={Eye}>
            <div className="space-y-4">
              <SettingsToggle
                label="Show Online Status"
                description="Let others see when you're online"
                icon={showOnlineStatus ? Eye : EyeOff}
                enabled={showOnlineStatus}
                onChange={setShowOnlineStatus}
              />
              <SettingsToggle
                label="Show Typing Indicator"
                description="Let others see when you're typing"
                icon={showTypingIndicator ? Eye : EyeOff}
                enabled={showTypingIndicator}
                onChange={setShowTypingIndicator}
              />
            </div>
          </SettingsSection>
          
          {/* Muted Channels */}
          <SettingsSection title="Muted Channels" icon={BellOff}>
            <div>
              {mutedChannels.length === 0 ? (
                <p className="text-sm text-slate-500">No muted channels</p>
              ) : (
                <div className="space-y-2">
                  {mutedChannels.map((channelId) => (
                    <div key={channelId} className="flex items-center justify-between py-2">
                      <span className="text-sm text-slate-700 dark:text-slate-300">Channel {channelId}</span>
                      <button className="text-xs text-violet-600 hover:text-violet-700">
                        Unmute
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SettingsSection>
        </div>
        
        {/* Save Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Settings
          </button>
        </div>
      </div>
    </div>
  )
}

// Settings Section Component
function SettingsSection({ 
  title, 
  icon: Icon, 
  children 
}: { 
  title: string
  icon: React.ElementType
  children: React.ReactNode 
}) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
        <Icon className="w-5 h-5 text-violet-600" />
        <h2 className="font-semibold text-slate-800 dark:text-white">{title}</h2>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  )
}

// Settings Toggle Component
function SettingsToggle({
  label,
  description,
  icon: Icon,
  enabled,
  onChange,
}: {
  label: string
  description: string
  icon: React.ElementType
  enabled: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-9 h-9 rounded-lg flex items-center justify-center",
          enabled 
            ? "bg-violet-100 dark:bg-violet-900/30 text-violet-600" 
            : "bg-slate-100 dark:bg-slate-700 text-slate-400"
        )}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="font-medium text-sm text-slate-800 dark:text-white">{label}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={cn(
          "relative w-11 h-6 rounded-full transition-colors",
          enabled ? "bg-violet-600" : "bg-slate-300 dark:bg-slate-600"
        )}
      >
        <div className={cn(
          "absolute top-1 w-4 h-4 rounded-full bg-white transition-transform",
          enabled ? "translate-x-6" : "translate-x-1"
        )} />
      </button>
    </div>
  )
}
